import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  getOrganization,
  updateOrganization,
  listOrgPositions,
  replaceOrgPositions,
  ApiError,
  type OrganizationProfile,
  type OrgEditInput,
  type OrgPosition,
} from "../api";
import { LogoUploader } from "./LogoUploader";
import { ActivityChipPicker } from "./ActivityChipPicker";
import type { ActivityKey } from "../activities";

/** Standard org officer slate seeded on first edit when an org has
 *  no positions yet. Mirrors how most US 501(c)3 trail nonprofits
 *  organize their board. Custom titles append below this list. */
const STANDARD_POSITION_TITLES = [
  "President",
  "Vice President",
  "Treasurer",
  "Secretary",
  "Trail Director",
] as const;

interface PositionDraft {
  /** Stable client-side row key for React. Server-side ids are not
   *  preserved across a replace-all save. */
  key: string;
  title: string;
  holderName: string;
  holderEmail: string;
  holderPhone: string;
}

function blankPosition(title: string): PositionDraft {
  return {
    key: crypto.randomUUID(),
    title,
    holderName: "",
    holderEmail: "",
    holderPhone: "",
  };
}

function positionsToDraft(positions: OrgPosition[]): PositionDraft[] {
  if (positions.length === 0) {
    // Seed the canonical slate so a brand-new org gets a starting
    // point — the curator just fills in names.
    return STANDARD_POSITION_TITLES.map((t) => blankPosition(t));
  }
  return positions.map((p) => ({
    key: p.id,
    title: p.title,
    holderName: p.holderName ?? "",
    holderEmail: p.holderEmail ?? "",
    holderPhone: p.holderPhone ?? "",
  }));
}

/**
 * Org-edit drawer — Phase C1.
 *
 * Right-slide on desktop, bottom sheet on mobile — same chassis the
 * curator TrailDrawer uses. Owners of any trail under the org can
 * edit. Fields are grouped HIG-style into:
 *
 *   Identity      — name + slug
 *   Branding      — logo URL + tagline mission
 *   Web presence  — website, contact email
 *   Social        — instagram, facebook, strava
 *   Support us    — donation link
 *
 * Logo upload to R2 is parked; for MVP an owner pastes a hosted URL.
 */

interface Draft {
  name: string;
  slug: string;
  logoUrl: string;
  websiteUrl: string;
  mission: string;
  contactEmail: string;
  instagramHandle: string;
  facebookUrl: string;
  stravaClubUrl: string;
  donationUrl: string;
  taxId: string;
  /** Sorted comma-joined snapshot for fast diffing. The render layer
   *  keeps a Set<ActivityKey> for toggle ergonomics — kept in sync
   *  via setDraft. */
  activitiesKey: string;
  activities: ActivityKey[];
}

function profileToDraft(p: OrganizationProfile): Draft {
  const acts = ((p.activities ?? []) as ActivityKey[]).slice().sort();
  return {
    name: p.name,
    slug: p.slug,
    logoUrl: p.logoUrl ?? "",
    websiteUrl: p.websiteUrl ?? "",
    mission: p.mission ?? "",
    contactEmail: p.contactEmail ?? "",
    instagramHandle: p.instagramHandle ?? "",
    facebookUrl: p.facebookUrl ?? "",
    stravaClubUrl: p.stravaClubUrl ?? "",
    donationUrl: p.donationUrl ?? "",
    taxId: p.taxId ?? "",
    activities: acts,
    activitiesKey: acts.join(","),
  };
}

function diff(original: Draft, draft: Draft): OrgEditInput {
  const out: OrgEditInput = {};
  if (draft.name.trim() !== original.name) out.name = draft.name.trim();
  if (draft.slug.trim() !== original.slug) out.slug = draft.slug.trim();
  if (draft.logoUrl !== original.logoUrl)
    out.logoUrl = draft.logoUrl.trim() || null;
  if (draft.websiteUrl !== original.websiteUrl)
    out.websiteUrl = draft.websiteUrl.trim() || null;
  if (draft.mission !== original.mission)
    out.mission = draft.mission.trim() || null;
  if (draft.contactEmail !== original.contactEmail)
    out.contactEmail = draft.contactEmail.trim() || null;
  if (draft.instagramHandle !== original.instagramHandle)
    out.instagramHandle = draft.instagramHandle.trim() || null;
  if (draft.facebookUrl !== original.facebookUrl)
    out.facebookUrl = draft.facebookUrl.trim() || null;
  if (draft.stravaClubUrl !== original.stravaClubUrl)
    out.stravaClubUrl = draft.stravaClubUrl.trim() || null;
  if (draft.donationUrl !== original.donationUrl)
    out.donationUrl = draft.donationUrl.trim() || null;
  if (draft.taxId !== original.taxId)
    out.taxId = draft.taxId.trim() || null;
  if (draft.activitiesKey !== original.activitiesKey)
    out.activities = draft.activities;
  return out;
}

export function OrgDrawer({
  orgId,
  onClose,
  onSaved,
  onEditTrail,
}: {
  orgId: string;
  onClose: () => void;
  onSaved: (updated: OrganizationProfile) => void;
  /** When provided, the "Trails" section renders tappable rows
   *  that fire this callback with the trail's slug. The parent is
   *  responsible for closing this drawer + opening TrailDrawer.
   *  Optional so non-launchpad call sites (e.g. AdminWorkspace) can
   *  skip the cross-drawer navigation. */
  onEditTrail?: (slug: string) => void;
}) {
  const [profile, setProfile] = useState<OrganizationProfile | null>(null);
  /** Trails that belong to this org. Fetched alongside the profile
   *  via the existing /admin/orgs/:id endpoint — no extra round-
   *  trip. Drives the Trails section render. */
  const [linkedTrails, setLinkedTrails] = useState<
    { slug: string; name: string; region: string | null; state: string | null }[]
  >([]);
  const [original, setOriginal] = useState<Draft | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  // Roster — loaded async right after the org profile resolves. Held
  // as a separate draft (instead of folded into Draft) so the
  // replace-all PUT can fire independently of the profile patch.
  const [originalPositions, setOriginalPositions] = useState<PositionDraft[] | null>(null);
  const [positionsDraft, setPositionsDraft] = useState<PositionDraft[] | null>(null);
  const [positionsError, setPositionsError] = useState<string | null>(null);

  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    getOrganization(orgId)
      .then((r) => {
        if (cancelled) return;
        setProfile(r.organization);
        setLinkedTrails(r.trails);
        const seed = profileToDraft(r.organization);
        setOriginal(seed);
        setDraft(seed);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err instanceof ApiError ? err.message : "Couldn't load the org."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Load the roster once the org profile resolves. Separate effect
  // so the org fetch failure doesn't block roster load, and the
  // initial draft seed uses the canonical 5-position slate when the
  // server returns an empty array.
  useEffect(() => {
    let cancelled = false;
    setPositionsError(null);
    listOrgPositions(orgId)
      .then((r) => {
        if (cancelled) return;
        const seeded = positionsToDraft(r.positions);
        setOriginalPositions(seeded);
        setPositionsDraft(seeded);
      })
      .catch((err) => {
        if (cancelled) return;
        setPositionsError(
          err instanceof ApiError ? err.message : "Couldn't load the roster."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  useLayoutEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [orgId, profile?.id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") requestClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const positionsDirty = useMemo(() => {
    if (!originalPositions || !positionsDraft) return false;
    if (originalPositions.length !== positionsDraft.length) return true;
    return positionsDraft.some((p, i) => {
      const o = originalPositions[i];
      return (
        p.title !== o.title ||
        p.holderName !== o.holderName ||
        p.holderEmail !== o.holderEmail ||
        p.holderPhone !== o.holderPhone
      );
    });
  }, [originalPositions, positionsDraft]);

  const dirty = useMemo(() => {
    if (!original || !draft) return false;
    if (Object.keys(diff(original, draft)).length > 0) return true;
    return positionsDirty;
  }, [original, draft, positionsDirty]);

  function requestClose() {
    if (
      dirty &&
      !confirm("You have unsaved changes. Bail out and lose them?")
    ) {
      return;
    }
    setOpen(false);
    setTimeout(onClose, 200);
  }

  async function handleSave() {
    if (!profile || !original || !draft) return;
    const patch = diff(original, draft);
    const profileChanged = Object.keys(patch).length > 0;
    if (!profileChanged && !positionsDirty) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (profileChanged) {
        const r = await updateOrganization(profile.id, patch);
        setProfile(r.organization);
        const seed = profileToDraft(r.organization);
        setOriginal(seed);
        setDraft(seed);
        onSaved(r.organization);
      }
      if (positionsDirty && positionsDraft) {
        const payload = positionsDraft.map((p, index) => ({
          title: p.title.trim(),
          holderName: p.holderName.trim() || null,
          holderEmail: p.holderEmail.trim() || null,
          holderPhone: p.holderPhone.trim() || null,
          positionOrder: (index + 1) * 10,
        }));
        const r = await replaceOrgPositions(profile.id, payload);
        const seeded = positionsToDraft(r.positions);
        setOriginalPositions(seeded);
        setPositionsDraft(seeded);
      }
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError(
        err instanceof ApiError ? err.message : "Couldn't save."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={`drawer-backdrop ${open ? "open" : ""}`}
      onClick={requestClose}
      aria-hidden={!open}
    >
      <aside
        className={`drawer ${open ? "open" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={profile ? `Edit ${profile.name}` : "Loading organization"}
      >
        <header className="drawer-head">
          <div>
            <div className="drawer-eyebrow">Organization</div>
            <h2 className="drawer-title">{profile?.name ?? "Loading…"}</h2>
            <div className="drawer-sub">
              How your org shows up to riders + crew.
            </div>
          </div>
          <button
            type="button"
            className="secondary small"
            onClick={requestClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="drawer-body" ref={bodyRef}>
          {loadError && (
            <div className="error" style={{ marginBottom: 12 }}>
              {loadError}
            </div>
          )}

          {draft && (
            <>
              <Section title="Identity" hint="Who you are.">
                <Field label="Organization name">
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  />
                </Field>
                <Field
                  label="Slug"
                  hint="Used in URLs. Lowercase letters, numbers, dashes only."
                >
                  <input
                    type="text"
                    value={draft.slug}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                      })
                    }
                  />
                </Field>
                <Field
                  label="Federal Tax ID (EIN)"
                  hint="Your 501(c)(3) number. Format: XX-XXXXXXX. Kept private — used later for donation-receipt automation."
                >
                  <input
                    type="text"
                    placeholder="12-3456789"
                    value={draft.taxId}
                    onChange={(e) => setDraft({ ...draft, taxId: e.target.value })}
                  />
                </Field>
              </Section>

              <Section
                title="Activities"
                hint="The disciplines you steward. Multi-select — a county parks dept might pick hiking + mountain biking + horseback, while a SORBA chapter might be mountain biking alone."
              >
                <ActivityChipPicker
                  selected={new Set<ActivityKey>(draft.activities)}
                  onToggle={(key) => {
                    const next = new Set<ActivityKey>(draft.activities);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    const sorted = [...next].sort() as ActivityKey[];
                    setDraft({
                      ...draft,
                      activities: sorted,
                      activitiesKey: sorted.join(","),
                    });
                  }}
                />
              </Section>

              <Section
                title="Trails"
                hint={
                  linkedTrails.length === 0
                    ? "No trails linked yet. Open a trail's drawer and pick this org from the Organization section."
                    : `${linkedTrails.length} trail${linkedTrails.length === 1 ? "" : "s"} stewarded by this org. Tap to edit one.`
                }
              >
                {linkedTrails.length === 0 ? (
                  <div className="muted" style={{ fontSize: 12 }}>
                    Link trails from their own drawer's Organization picker.
                  </div>
                ) : (
                  <ul className="org-trails-list">
                    {linkedTrails.map((t) => (
                      <li key={t.slug}>
                        <button
                          type="button"
                          className="org-trails-row"
                          disabled={!onEditTrail}
                          onClick={() => {
                            // Hand the slug back up to the launchpad.
                            // The parent closes this drawer + opens
                            // TrailDrawer so we don't end up with
                            // two stacked editors.
                            onEditTrail?.(t.slug);
                          }}
                        >
                          <div className="org-trails-row-main">
                            <div className="org-trails-row-name">{t.name}</div>
                            <div className="org-trails-row-sub muted">
                              {[t.region, t.state].filter(Boolean).join(", ") ||
                                "No pin yet"}
                            </div>
                          </div>
                          {onEditTrail && (
                            <span
                              className="org-trails-row-chev"
                              aria-hidden="true"
                            >
                              ›
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="Branding" hint="What riders see on the trail.">
                {profile && (
                  <LogoUploader
                    orgId={profile.id}
                    currentLogoUrl={draft.logoUrl || null}
                    onUploaded={(newUrl) => {
                      // Capture whether the user had OTHER unsaved
                      // edits at the moment the upload completed. We
                      // use this to decide whether to auto-close the
                      // drawer afterwards (the upload itself is
                      // already persisted server-side, so if it was
                      // the only thing in flight there's no
                      // "remaining" Save action — that ghost Save
                      // button is what was annoying Eric).
                      const hadOtherEdits = dirty;

                      setDraft((d) => (d ? { ...d, logoUrl: newUrl } : d));
                      setOriginal((o) => (o ? { ...o, logoUrl: newUrl } : o));
                      const refreshed = { ...profile, logoUrl: newUrl };
                      setProfile(refreshed);
                      onSaved(refreshed);

                      // Auto-close once the cropper's "Looks epic ✨"
                      // celebration has played, IF the upload was the
                      // whole story. Otherwise leave the drawer open
                      // so the user can finish their other edits.
                      if (!hadOtherEdits) {
                        setTimeout(() => {
                          setOpen(false);
                          setTimeout(onClose, 200);
                        }, 1100);
                      }
                    }}
                  />
                )}
                <details className="logo-url-fallback">
                  <summary>…or paste a hosted URL instead</summary>
                  <Field label="Logo URL">
                    <input
                      type="url"
                      value={draft.logoUrl}
                      placeholder="https://"
                      onChange={(e) =>
                        setDraft({ ...draft, logoUrl: e.target.value })
                      }
                    />
                  </Field>
                </details>
                <Field
                  label="Mission / tagline"
                  hint="One or two sentences. Shows on the trail detail screen."
                >
                  <textarea
                    rows={3}
                    value={draft.mission}
                    placeholder="e.g. Volunteer-built singletrack since 1994."
                    onChange={(e) =>
                      setDraft({ ...draft, mission: e.target.value })
                    }
                  />
                </Field>
              </Section>

              <Section title="Web presence">
                <Field label="Website">
                  <input
                    type="url"
                    value={draft.websiteUrl}
                    placeholder="https://"
                    onChange={(e) => setDraft({ ...draft, websiteUrl: e.target.value })}
                  />
                </Field>
                <Field label="Contact email">
                  <input
                    type="email"
                    value={draft.contactEmail}
                    placeholder="info@example.com"
                    onChange={(e) =>
                      setDraft({ ...draft, contactEmail: e.target.value })
                    }
                  />
                </Field>
              </Section>

              <Section title="Social">
                <Field label="Instagram handle">
                  <input
                    type="text"
                    value={draft.instagramHandle}
                    placeholder="@yourtrails"
                    onChange={(e) =>
                      setDraft({ ...draft, instagramHandle: e.target.value })
                    }
                  />
                </Field>
                <Field label="Facebook page URL">
                  <input
                    type="url"
                    value={draft.facebookUrl}
                    placeholder="https://facebook.com/"
                    onChange={(e) =>
                      setDraft({ ...draft, facebookUrl: e.target.value })
                    }
                  />
                </Field>
                <Field label="Strava club URL">
                  <input
                    type="url"
                    value={draft.stravaClubUrl}
                    placeholder="https://strava.com/clubs/"
                    onChange={(e) =>
                      setDraft({ ...draft, stravaClubUrl: e.target.value })
                    }
                  />
                </Field>
              </Section>

              <Section
                title="Roster"
                hint="The org's officers. Names + contact info stay private — used for internal coordination and (later) donation-receipt routing."
              >
                {positionsError && (
                  <div className="error" style={{ marginBottom: 8 }}>
                    {positionsError}
                  </div>
                )}
                {positionsDraft === null ? (
                  <div className="muted">Loading roster…</div>
                ) : (
                  <>
                    <div className="org-roster-list">
                      {positionsDraft.map((p, index) => (
                        <div key={p.key} className="org-roster-row">
                          <div className="org-roster-row-head">
                            <input
                              type="text"
                              className="org-roster-title"
                              placeholder="Position title"
                              value={p.title}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPositionsDraft((prev) =>
                                  prev
                                    ? prev.map((q, i) =>
                                        i === index ? { ...q, title: value } : q
                                      )
                                    : prev
                                );
                              }}
                            />
                            <button
                              type="button"
                              className="ghost org-roster-remove"
                              title="Remove this position"
                              onClick={() => {
                                setPositionsDraft((prev) =>
                                  prev ? prev.filter((_, i) => i !== index) : prev
                                );
                              }}
                            >
                              ✕
                            </button>
                          </div>
                          <div className="org-roster-row-fields">
                            <input
                              type="text"
                              placeholder="Name"
                              value={p.holderName}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPositionsDraft((prev) =>
                                  prev
                                    ? prev.map((q, i) =>
                                        i === index ? { ...q, holderName: value } : q
                                      )
                                    : prev
                                );
                              }}
                            />
                            <input
                              type="email"
                              placeholder="Email"
                              value={p.holderEmail}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPositionsDraft((prev) =>
                                  prev
                                    ? prev.map((q, i) =>
                                        i === index ? { ...q, holderEmail: value } : q
                                      )
                                    : prev
                                );
                              }}
                            />
                            <input
                              type="tel"
                              placeholder="Phone"
                              value={p.holderPhone}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPositionsDraft((prev) =>
                                  prev
                                    ? prev.map((q, i) =>
                                        i === index ? { ...q, holderPhone: value } : q
                                      )
                                    : prev
                                );
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="ghost"
                      style={{ marginTop: 8 }}
                      onClick={() => {
                        setPositionsDraft((prev) =>
                          prev ? [...prev, blankPosition("")] : prev
                        );
                      }}
                    >
                      + Add position
                    </button>
                  </>
                )}
              </Section>

              <Section
                title="Support us"
                hint="Riders see a 'Donate' link on the trail screen when set."
              >
                <Field label="Donation URL">
                  <input
                    type="url"
                    value={draft.donationUrl}
                    placeholder="https://"
                    onChange={(e) =>
                      setDraft({ ...draft, donationUrl: e.target.value })
                    }
                  />
                </Field>
              </Section>
            </>
          )}
        </div>

        <footer className="drawer-foot">
          <div className="drawer-foot-actions">
            <span />
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="secondary"
                onClick={requestClose}
                disabled={saving}
              >
                Bail out
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
          <div className="drawer-foot-status">
            {saving ? (
              <span className="muted">Saving…</span>
            ) : saveError ? (
              <span className="error">{saveError}</span>
            ) : savedAt ? (
              <span className="muted">Saved · {timeAgo(savedAt)}</span>
            ) : dirty ? (
              <span className="muted">Unsaved changes</span>
            ) : (
              <span className="muted">No changes yet</span>
            )}
          </div>
        </footer>
      </aside>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="drawer-section">
      <header>
        <h3>{title}</h3>
        {hint && <p className="muted">{hint}</p>}
      </header>
      <div className="drawer-section-body">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="drawer-field">
      <span className="drawer-field-label">{label}</span>
      {children}
      {hint && <span className="drawer-field-hint">{hint}</span>}
    </label>
  );
}

function timeAgo(ts: number): string {
  const delta = Math.max(0, Date.now() - ts);
  const seconds = Math.floor(delta / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
}
